import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/travis/build/sgrishchenko/reselect-utils/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "structured--sequence-selectors"
    }}>{`Structured & Sequence Selectors`}</h1>
    <h2 {...{
      "id": "structured-selector"
    }}>{`Structured Selector`}</h2>
    <p>{`If you ever used `}<a parentName="p" {...{
        "href": "https://github.com/reduxjs/reselect"
      }}>{`Reselect`}</a>{`, you must be familiar with `}<a parentName="p" {...{
        "href": "https://github.com/reduxjs/reselect#createstructuredselectorinputselectors-selectorcreator--createselector"
      }}>{`Structured Selector`}</a>{`. Reselect Utils provides implementation of this helper but with improved typings. You can use `}<inlineCode parentName="p">{`Structured Selector`}</inlineCode>{` from this library with heterogeneous selectors. Heterogeneous selectors are selectors with different types of state. See example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`type PersonsStateSlice = {
  persons: Record<number, Person>;
};

const personsSelector = (state: PersonsStateSlice) => state.persons;

type MessagesStateSlice = {
  messages: Record<number, Message>;
};

const messagesSelector = (state: MessagesStateSlice) => state.messages;
`}</code></pre>
    <p>{`Now we can write a selector what select data from both state slices:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { createStructuredSelector } from 'reselect-utils';

const personsAndMessagesSelector = createStructuredSelector({
  persons: personsSelector,
  messages: messagesSelector,
});
`}</code></pre>
    <p>{`You can also use alias to reduce code:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { struct } from 'reselect-utils';

const personsAndMessagesSelector = struct({
  persons: personsSelector,
  messages: messagesSelector,
});
`}</code></pre>
    <h2 {...{
      "id": "cached-structured-selector"
    }}>{`Cached Structured Selector`}</h2>
    <p>{`If you use a parametric selector, you can use `}<a parentName="p" {...{
        "href": "https://github.com/toomuchdesign/re-reselect"
      }}>{`Re-reselect`}</a>{`. Reselect Utils provides `}<inlineCode parentName="p">{`Cached Structured Selector`}</inlineCode>{` for these purposes. For example, we have next selectors:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { createCachedSelector } from 're-reselect';

const personSelector = createCachedSelector(
  personsSelector,
  (state: PersonsStateSlice, props: Props) => props.personId,
  (persons, personId) => persons[personId],
)({
  keySelector: (state, props) => props.personId,
});

const messageSelector = createCachedSelector(
  messagesSelector,
  (state: MessagesStateSlice, props: Props) => props.messageId,
  (messages, messageId) => messages[messageId],
)({
  keySelector: (state, props) => props.messageId,
});
`}</code></pre>
    <p>{`You can write composing selector this way:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { cachedStruct } from 'reselect-utils';

const personAndMessageSelector = cachedStruct({
  persons: personSelector,
  messages: messageSelector,
})({ keySelector: (state, props) => \`\${props.personId}:\${props.messageId}\` });
`}</code></pre>
    <p><inlineCode parentName="p">{`cachedStruct`}</inlineCode>{` is short alias for `}<inlineCode parentName="p">{`Cached Structured Selector`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "sequence-selector"
    }}>{`Sequence Selector`}</h2>
    <p>{`There is also another type of structure besides objects - arrays. Reselect Utils provides `}<inlineCode parentName="p">{`Sequence Selector`}</inlineCode>{` helper for them:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { createCachedSelector } from 're-reselect';
import { bound, createSequenceSelector } from 'reselect-utils';

const personSelector = createCachedSelector(
  personsSelector,
  (state: PersonsStateSlice, props: Props) => props.personId,
  (persons, personId) => persons[personId],
)({
  keySelector: (state, props) => props.personId,
});

const firstPersonSelector = bound(personSelector, { personId: 1 });
const secondPersonSelector = bound(personSelector, { personId: 2 });

const firstTwoPersonsSelector = createSequenceSelector([
  firstPersonSelector,
  secondPersonSelector,
]);
`}</code></pre>
    <p><inlineCode parentName="p">{`Bound Selector`}</inlineCode>{` is described `}<a parentName="p" {...{
        "href": "/reselect-utils/docz/guides/bound-and-adapted-selectors#bound-selector"
      }}>{`here`}</a>{`. Now if you have next state, you will receive next result:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`const state = {
  persons: {
    1: {
      id: 1,
      firstName: 'Marry',
      secondName: 'Poppins',
    },
    2: {
      id: 2,
      firstName: 'Harry',
      secondName: 'Potter',
    },
  },
};

firstTwoPersonsSelector(state); // => [{ firstName: 'Marry', ... }, { firstName: 'Harry', ... }]
`}</code></pre>
    <h2 {...{
      "id": "cached-sequence-selector"
    }}>{`Cached Sequence Selector`}</h2>
    <p>{`If you try to combine parametric selectors, there is a cached version of `}<inlineCode parentName="p">{`Sequence Selector`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`import { createCachedSelector } from 're-reselect';
import { path, prop, bound, cachedSeq } from 'reselect-utils';

type Props = {
  personId: number;
  postfix: string;
};

const personFullNameSelector = createCachedSelector(
  path(personSelector).firstName(),
  path(personSelector).secondName(),
  prop<Props>().postfix(),
  (firstName, secondName, postfix) => \`\${firstName} \${secondName} (\${postfix})\`,
)({
  keySelector: (state, props) => \`\${props.personId}:\${props.postfix}\`,
});

const firstPersonFullNameSelector = bound(personFullNameSelector, {
  personId: 1,
});
const secondPersonFullNameSelector = bound(personFullNameSelector, {
  personId: 2,
});

const firstTwoPersonFullNamesSelector = cachedSeq([
  firstPersonFullNameSelector,
  secondPersonFullNameSelector,
])({
  keySelector: prop<Props>().postfix(),
});

firstTwoPersonsSelector(state, { postfix: '*' }); // => ['Marry Poppins (*)', 'Harry Potter (*)' }]

firstTwoPersonsSelector(state, { postfix: '?' }); // => ['Marry Poppins (?)', 'Harry Potter (?)' }]
`}</code></pre>
    <p><inlineCode parentName="p">{`Path Selector`}</inlineCode>{` and `}<inlineCode parentName="p">{`Prop Selector`}</inlineCode>{` are described `}<a parentName="p" {...{
        "href": "/reselect-utils/docz/guides/path-and-prop-selectors"
      }}>{`here`}</a>{`.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      